import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LanguageSwitcher, SpaceJoinForm, Badge } from "@/components/common";
import { Users, ArrowRight, ShieldCheck, MessagesSquare, Eye } from "lucide-react";

export default function Invite() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const org = params.get("org");
  const { user, ready, refresh } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [space, setSpace] = useState(null);
  const [areas, setAreas] = useState([]);
  const [debates, setDebates] = useState([]);

  useEffect(() => { api.get(`/spaces/${slug}`).then((r) => setSpace(r.data)).catch(() => setSpace(false)); }, [slug]);
  useEffect(() => { api.get("/policy-areas").then((r) => setAreas(r.data.areas)).catch(() => {}); }, []);
  useEffect(() => { if (space && space.id) api.get(`/debates?space_id=${space.id}`).then((r) => setDebates(r.data)).catch(() => {}); }, [space]);

  if (space === false) return <Centered>{t("Invitation not found.")}</Centered>;
  const nextPath = `/invite/${slug}${org ? `?org=${encodeURIComponent(org)}` : ""}`;
  const topics = (space?.topics && space.topics.length) ? space.topics : areas;

  const onJoined = async () => { await refresh(); nav(`/space/${slug}`); };
  const debateLink = (d) => `/debates/${d.id}`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex justify-end mb-4"><LanguageSwitcher /></div>
        <div className="border border-border bg-white p-8 text-center">
          {org ? <ShieldCheck className="mx-auto text-secondary mb-4" size={36} /> : <Users className="mx-auto text-secondary mb-4" size={36} />}
          <p className="overline mb-2">{org ? t("You're invited to co-organise") : t("You're invited to join")}</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-3">{space?.name || "…"}</h1>
          <p className="text-sm text-muted-foreground mb-2">{space?.description}</p>
          {org && <p className="text-sm text-foreground mb-4">{t("Organisers can manage the space's discussion topics and invite others.")}</p>}
          {space?.parties?.length > 0 && <p className="text-xs text-muted-foreground mb-6">{t("Participating parties:")} {space.parties.map((p) => p.name).join(", ")}</p>}
          {!ready ? <p className="text-muted-foreground">…</p> : user ? (
            <SpaceJoinForm slug={slug} topics={topics} organiserToken={org} onJoined={onJoined} />
          ) : (
            <div className="flex flex-col gap-3 items-center">
              <Link to={`/register?invite=${slug}`} data-testid="invite-register-btn" className="bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2 w-full justify-center">{t("Register & join")} <ArrowRight size={18} /></Link>
              <Link to={`/space/${slug}`} data-testid="invite-view-space" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"><Eye size={14} /> {t("See the space first — no account needed")}</Link>
              <Link to={`/login?next=${encodeURIComponent(nextPath)}`} className="text-sm text-muted-foreground font-semibold">{t("I already have an account")}</Link>
            </div>
          )}

          {debates.length > 0 && (
            <div className="mt-8 text-left border-t border-border pt-6" data-testid="invite-debates-preview">
              <p className="overline mb-1 flex items-center gap-1.5"><Eye size={13} /> {t("Debates in this space")}</p>
              <p className="text-xs text-muted-foreground mb-3">{t("You can read these debates without joining. Join to take part in the discussion and votes.")}</p>
              <div className="space-y-2">
                {debates.slice(0, 8).map((d) => (
                  <Link key={d.id} to={debateLink(d)} data-testid="invite-debate-card" className="block border border-border rounded-sm p-3 hover:border-primary hover:-translate-y-0.5 transition-all">
                    <div className="flex flex-wrap items-center gap-2 mb-1"><Badge tone="blue">{t(d.stage.replace("_", " "))}</Badge><Badge tone="primary">{t(d.policy_area)}</Badge></div>
                    <p className="text-sm font-semibold text-foreground">{d.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MessagesSquare size={12} /> {t("{{count}} contributions", { count: d.argument_count })}</p>
                  </Link>
                ))}
              </div>
              {user && <Link to={`/space/${slug}`} data-testid="invite-browse-space" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">{t("Browse the whole space (view only)")} <ArrowRight size={14} /></Link>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Centered({ children }) { return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{children}</div>; }
